import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Music2, Maximize2, Plus, Bell, Link2, ChevronLeft } from 'lucide-react';
import { songsAPI } from '../utils/api';
import SongViewerModal from './SongViewerModal';
import SongPickerDialog from './SongPickerDialog';
import SongDetailPanel from './SongDetailPanel';
import { Button } from './ui/button';

/**
 * Setlist tab — shows service-level song list with key/tempo overrides.
 * Songs are managed independently from program items. When a song is added
 * to a program item, it is also auto-added to the setlist in ServiceDetail.
 */
const SetlistTab = ({
  serviceId,        // needed for file uploads
  setlist,          // [{ song_id, key, tempo }]
  onSetlistChange,  // (newSetlist) => void
  hasEditPermission,
  songs,            // full library (for picker)
  getSongMeta,      // (songId) => song metadata
  order,            // service program order (to show which item each song is in)
  onNotifySetlistChange,
  notifyingSongs,
}) => {
  const navigate = useNavigate();
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [contents, setContents] = useState({});
  const [loadingContent, setLoadingContent] = useState({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedSongIdx, setSelectedSongIdx] = useState(null); // index into setlistSongs

  // Restore selectedSongIdx when the user presses browser back
  useEffect(() => {
    const handlePopState = (event) => {
      const state = event.state?.serviceDetail;
      if (!state || state.tab !== 'setlist') {
        setSelectedSongIdx(null);
      } else {
        setSelectedSongIdx(state.songIdx ?? null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleSelectSong = useCallback((idx) => {
    window.history.pushState({ serviceDetail: { tab: 'setlist', songIdx: idx } }, '');
    setSelectedSongIdx(idx);
  }, []);

  // Map song_id → item names from the program order
  const songToItems = useMemo(() => {
    const map = {};
    for (const entry of (order || [])) {
      if (entry.type === 'item') {
        for (const s of (entry.songs || [])) {
          if (!map[s.song_id]) map[s.song_id] = [];
          map[s.song_id].push(entry.name || 'Unnamed Item');
        }
      }
    }
    return map;
  }, [order]);
  const setlistSongs = useMemo(() => {
    // Collect song_ids in program order (deduplicated)
    const orderedIds = [];
    const inProgram = new Set();
    for (const entry of (order || [])) {
      if (entry.type === 'item') {
        for (const s of (entry.songs || [])) {
          if (!inProgram.has(s.song_id)) {
            inProgram.add(s.song_id);
            orderedIds.push(s.song_id);
          }
        }
      }
    }
    // Fast lookup map of setlist entries
    const setlistMap = new Map((setlist || []).map(s => [s.song_id, s]));
    // Program songs first (in program order), then unassigned at bottom
    const sorted = [
      ...orderedIds.filter(id => setlistMap.has(id)).map(id => setlistMap.get(id)),
      ...(setlist || []).filter(s => !inProgram.has(s.song_id)),
    ];
    return sorted.map((entry, idx) => {
      const meta = getSongMeta(entry.song_id) || {};
      return {
        ...meta,
        song_id: entry.song_id,
        key: entry.key !== undefined ? entry.key : (meta.key || ''),
        bpm: (entry.tempo != null && entry.tempo !== '') ? entry.tempo : (meta.bpm || ''),
        originalBpm: meta.bpm || '',
        accidental: entry.accidental || null,
        links: entry.links || [],
        attachments: entry.attachments || [],
        _idx: idx,
      };
    }).filter(s => s.title);
  }, [setlist, order, getSongMeta]);

  // Library songs not already in setlist (for picker)
  const availableForSetlist = useMemo(() => {
    const inSetlist = new Set((setlist || []).map(s => s.song_id));
    return (songs || []).filter(s => !inSetlist.has(s.song_id));
  }, [setlist, songs]);

  const addSongToSetlist = useCallback((songId) => {
    const meta = (songs || []).find(s => s.song_id === songId) || {};
    const newEntry = { song_id: songId, key: meta.key || '', tempo: meta.bpm || '' };
    onSetlistChange([...(setlist || []), newEntry]);
    setPickerOpen(false);
  }, [setlist, songs, onSetlistChange]);

  const removeFromSetlist = useCallback((songId) => {
    onSetlistChange((setlist || []).filter(s => s.song_id !== songId));
  }, [setlist, onSetlistChange]);

  const updateSetlistSong = useCallback((songId, field, value) => {
    onSetlistChange((setlist || []).map(s => s.song_id === songId ? { ...s, [field]: value } : s));
  }, [setlist, onSetlistChange]);

  const handleAddLink = useCallback((songId, { url, label }) => {
    const entry = (setlist || []).find(s => s.song_id === songId);
    const existing = entry?.links || [];
    onSetlistChange((setlist || []).map(s =>
      s.song_id === songId ? { ...s, links: [...existing, { url, label }] } : s
    ));
  }, [setlist, onSetlistChange]);

  const removeLinkFromSong = useCallback((songId, linkIdx) => {
    onSetlistChange((setlist || []).map(s =>
      s.song_id === songId ? { ...s, links: (s.links || []).filter((_, i) => i !== linkIdx) } : s
    ));
  }, [setlist, onSetlistChange]);



  const fetchContent = useCallback(async (songId) => {
    if (contents[songId] !== undefined || loadingContent[songId]) return;
    setLoadingContent(prev => ({ ...prev, [songId]: true }));
    try {
      const res = await songsAPI.getById(songId);
      const content = res.data.content || '';
      setContents(prev => ({ ...prev, [songId]: content }));
      // Keep cache fresh for offline use
      try { localStorage.setItem(`song_content_${songId}`, content); } catch { /* quota */ }
    } catch {
      // Offline fallback — serve stale cache if available
      try {
        const cached = localStorage.getItem(`song_content_${songId}`);
        setContents(prev => ({ ...prev, [songId]: cached || '' }));
      } catch {
        setContents(prev => ({ ...prev, [songId]: '' }));
      }
    } finally {
      setLoadingContent(prev => ({ ...prev, [songId]: false }));
    }
  }, [contents, loadingContent]);

  // Open the viewer with a clean content cache so any song edits made since
  // the last viewer session (same tab, different tab, different device) are
  // always reflected. Within the open viewer, the cache still works normally
  // for smooth song-to-song navigation.
  const handleOpenViewer = (idx = 0) => {
    setContents({});
    setLoadingContent({});
    setSelectedSongIdx(null); // close detail panel if open
    setViewerIndex(idx);
    window.history.pushState({ viewer: 'song' }, '');
    setViewerOpen(true);
  };

  const empty = setlistSongs.length === 0;
  const selectedSong = selectedSongIdx !== null ? setlistSongs[selectedSongIdx] : null;

  return (
    <div data-testid="setlist-tab">

      {/* ── Header row — always visible ───────────────────────── */}
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        {/* Left: back link when in detail, song count otherwise */}
        {selectedSong ? (
          <button
            onClick={() => navigate(-1)}
            data-testid="song-detail-back-btn"
            className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Setlist
          </button>
        ) : (
          <p className="text-sm text-slate-400">
            {empty ? 'No songs yet' : `${setlistSongs.length} song${setlistSongs.length !== 1 ? 's' : ''}`}
          </p>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          {hasEditPermission && (
            <Button variant="outline" size="sm" data-testid="notify-setlist-change-btn"
              className="rounded-full gap-1.5 h-8 text-xs disabled:opacity-60 disabled:cursor-not-allowed"
              onClick={onNotifySetlistChange} disabled={notifyingSongs}>
              {notifyingSongs
                ? <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                : <Bell className="w-3.5 h-3.5" />
              }
              <span className="hidden sm:inline">{notifyingSongs ? 'Sending...' : 'Notify Setlist Change'}</span>
            </Button>
          )}
          {!empty && (
            <button onClick={() => handleOpenViewer(selectedSongIdx ?? 0)} data-testid="setlist-fullscreen-btn"
              className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-white text-sm font-medium rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors">
              <Maximize2 className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Open Fullscreen</span>
            </button>
          )}
          {hasEditPermission && (
            <button onClick={() => setPickerOpen(true)} data-testid="setlist-add-song-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-700 text-white text-sm font-medium rounded-lg hover:bg-green-800 transition-colors">
              <Plus className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Add Song</span>
            </button>
          )}
        </div>
      </div>

      {/* ── Song detail card view (replaces list) ─────────────── */}
      {selectedSong ? (
        <SongDetailPanel
          serviceId={serviceId}
          song={selectedSong}
          programItems={songToItems[selectedSong.song_id] || []}
          hasEditPermission={hasEditPermission}
          onClose={() => navigate(-1)}
          onUpdateKey={(key) => updateSetlistSong(selectedSong.song_id, 'key', key)}
          onUpdateTempo={(tempo) => updateSetlistSong(selectedSong.song_id, 'tempo', tempo)}
          onUpdateAccidental={(a) => updateSetlistSong(selectedSong.song_id, 'accidental', a)}
          onAddLink={(link) => handleAddLink(selectedSong.song_id, link)}
          onRemoveLink={(linkIdx) => removeLinkFromSong(selectedSong.song_id, linkIdx)}
          onAddAttachment={(attachment) => {
            onSetlistChange((setlist || []).map(s =>
              s.song_id === selectedSong.song_id ? { ...s, attachments: [...(s.attachments || []), attachment] } : s
            ));
          }}
          onRemoveAttachment={(attachmentId) => {
            onSetlistChange((setlist || []).map(s =>
              s.song_id === selectedSong.song_id ? { ...s, attachments: (s.attachments || []).filter(a => a.attachment_id !== attachmentId) } : s
            ));
          }}
          onOpenViewer={() => handleOpenViewer(selectedSongIdx)}
          onRemove={() => { removeFromSetlist(selectedSong.song_id); navigate(-1); }}
          contents={contents}
          loadingContent={loadingContent}
          onFetchContent={fetchContent}
        />
      ) : (
        <>
          {/* ── Song list ──────────────────────────────────────── */}
          {empty ? (
            <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="setlist-empty">
              <div className="w-14 h-14 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                <Music2 className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-600 dark:text-slate-300">No songs in the setlist yet</p>
              {hasEditPermission ? (
                <button onClick={() => setPickerOpen(true)} data-testid="setlist-add-song-empty-btn"
                  className="mt-3 text-sm text-green-600 hover:text-green-800 font-medium">
                  Add songs to the setlist
                </button>
              ) : (
                <p className="text-xs text-slate-400 mt-1">Songs added to program items appear here</p>
              )}
            </div>
          ) : (
            <div className="rounded-xl border border-slate-200 dark:border-white/10 overflow-hidden divide-y divide-slate-200 dark:divide-white/10" data-testid="setlist-songs-container">
              {setlistSongs.map((song, idx) => (
                <div key={`${song.song_id}-${idx}`} data-testid={`setlist-song-${song.song_id}`}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-slate-200 dark:bg-white/10 text-slate-600 dark:text-slate-300 text-xs font-semibold flex items-center justify-center">
                    {idx + 1}
                  </span>
                  <button
                    onClick={() => handleSelectSong(idx)}
                    data-testid={`setlist-song-btn-${song.song_id}`}
                    className="flex-1 min-w-0 text-left"
                  >
                    <p className="text-sm font-medium text-slate-900 dark:text-white break-words leading-snug">{song.title}</p>
                    {song.artist && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 break-words">{song.artist}</p>
                    )}
                    {songToItems[song.song_id]?.length > 0 && (
                      <p className="text-xs text-slate-400 dark:text-slate-500 break-words mt-0.5">
                        {songToItems[song.song_id].join(', ')}
                      </p>
                    )}
                    {(song.key || song.bpm || song.links?.length > 0 || song.attachments?.length > 0) && (
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        {song.key && (
                          <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 text-xs font-mono font-semibold rounded">
                            {song.key}
                          </span>
                        )}
                        {song.bpm && (
                          <span className="px-1.5 py-0.5 bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-slate-400 text-xs font-mono rounded">
                            {song.bpm} BPM
                          </span>
                        )}
                        {(song.links?.length > 0 || song.attachments?.length > 0) && (
                          <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-slate-100 dark:bg-white/10 text-slate-400 dark:text-slate-500 text-xs rounded">
                            <Link2 className="w-2.5 h-2.5" />
                            {(song.links?.length || 0) + (song.attachments?.length || 0)}
                          </span>
                        )}
                      </div>
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Add song picker */}
      <SongPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        availableSongs={availableForSetlist}
        totalSongs={(songs || []).length}
        onAddSong={addSongToSetlist}
        title="Add Song to Setlist"
      />

      {/* Full-screen viewer */}
      {viewerOpen && (
        <SongViewerModal
          songs={setlistSongs}
          contents={contents}
          loadingContent={loadingContent}
          initialIndex={viewerIndex}
          onClose={() => setViewerOpen(false)}
          onFetchContent={fetchContent}
        />
      )}
    </div>
  );
};

export default SetlistTab;
